import React, { useState, useMemo, useCallback } from "react";
import { ScrollView, View, TouchableOpacity, StyleSheet, TextInput } from "react-native";
import NSText from "./NSText";
import { AntDesign } from "@expo/vector-icons";
import { Feather } from "@expo/vector-icons";

export interface NestedDataType {
  id: string;
  name: string;
  isDisable: boolean;
  children: NestedDataType[] | null;
}

const INDENT_PER_LEVEL = 10;

const shadowStyle = {
  marginBottom: 2,
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.25,
  shadowRadius: 3.84,
  elevation: 5,
};

// Shallow search: only matches top-level item names
const filterShallow = (data: NestedDataType[], term: string): NestedDataType[] => {
  return data.filter(item => item.name.toLowerCase().includes(term));
};

// Deep search: walks the tree to arbitrary depth. If a node matches, keep its
// entire subtree as-is. Otherwise keep the node only if some descendant
// matches, with non-matching siblings pruned.
const filterDeep = (data: NestedDataType[], term: string): NestedDataType[] => {
  return data.reduce<NestedDataType[]>((acc, item) => {
    if (item.name.toLowerCase().includes(term)) {
      acc.push(item);
      return acc;
    }
    if (item.children?.length) {
      const filteredChildren = filterDeep(item.children, term);
      if (filteredChildren.length > 0) {
        acc.push({ ...item, children: filteredChildren });
      }
    }
    return acc;
  }, []);
};

interface SelectionBoxProps {
  isSelected: boolean;
  isDisabled: boolean;
  onPress: () => void;
}

const SelectionBox: React.FC<SelectionBoxProps> = ({
  isSelected,
  isDisabled,
  onPress,
}) => (
  <TouchableOpacity
    disabled={isDisabled}
    onPress={onPress}
    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
    style={styles.selectionBoxContainer}
  >
    <View
      style={[
        styles.selectionBox,
        isSelected && styles.selectionBoxSelected,
        isDisabled && styles.selectionBoxDisabled,
      ]}
    >
      {isSelected ? (
        <AntDesign name="check" size={10} color="white" />
      ) : null}
    </View>
  </TouchableOpacity>
);

interface RowProps {
  item: NestedDataType;
  depth: number;
  indexInParent: number;
  isFirstAtRoot: boolean;
  isLastAtRoot: boolean;
  hasSearch: boolean;
  isSearching: boolean;
  openedIndices: number[];
  toggleAtDepth: (depth: number, index: number) => void;
  selectedId?: string;
  onItemPress: (value: any, index: number) => void;
  closeDropDown: () => void;
  allowParentSelection?: boolean;
}

const Row: React.FC<RowProps> = ({
  item,
  depth,
  indexInParent,
  isFirstAtRoot,
  isLastAtRoot,
  hasSearch,
  isSearching,
  openedIndices,
  toggleAtDepth,
  selectedId,
  onItemPress,
  closeDropDown,
  allowParentSelection,
}) => {
  const hasChildren = !!item?.children && item.children.length > 0;
  const isExpanded = isSearching || openedIndices[depth] === indexInParent;
  const paddingStart = (depth + 1) * INDENT_PER_LEVEL;
  const isSelected = selectedId === item?.id;
  const isRoot = depth === 0;

  // Rounded corners apply only to the first/last items at the root level
  const cornerStyle = isRoot
    ? {
        borderTopStartRadius: isFirstAtRoot && !hasSearch ? 5 : 0,
        borderTopEndRadius: isFirstAtRoot && !hasSearch ? 5 : 0,
        borderBottomStartRadius: isLastAtRoot ? 5 : 0,
        borderBottomEndRadius: isLastAtRoot ? 5 : 0,
      }
    : {};

  if (hasChildren) {
    if (allowParentSelection) {
      // Checkbox selects the parent (single-select). Tapping the row expands/collapses.
      return (
        <View style={styles.rowContainer}>
          <View
            style={[
              styles.expandableRow,
              { paddingStart },
              cornerStyle,
            ]}
          >
            <SelectionBox
              isSelected={isSelected}
              isDisabled={item.isDisable}
              onPress={() => {
                onItemPress(item, indexInParent);
              }}
            />
            <TouchableOpacity
              disabled={item.isDisable}
              style={styles.expandableLabel}
              onPress={() => {
                if (isSearching) return;
                toggleAtDepth(depth, indexInParent);
              }}
            >
              <NSText
                style={{
                  maxWidth: "90%",
                  flexShrink: 1,
                  marginEnd: 5,
                  fontSize: 12,
                  color: item.isDisable ? "#BDBBBF" : "#303030",
                }}
                text={item?.name}
              />
              <AntDesign
                name={isExpanded ? "caret-up" : "caret-down"}
                size={10}
                color={item.isDisable ? "#BDBBBF" : "#303030"}
              />
            </TouchableOpacity>
          </View>
          {isExpanded ? (
            <ScrollView showsVerticalScrollIndicator={false}>
              {item.children!.map((child, idx) => (
                <Row
                  key={`${child.id}-${depth + 1}-${idx}`}
                  item={child}
                  depth={depth + 1}
                  indexInParent={idx}
                  isFirstAtRoot={false}
                  isLastAtRoot={false}
                  hasSearch={hasSearch}
                  isSearching={isSearching}
                  openedIndices={openedIndices}
                  toggleAtDepth={toggleAtDepth}
                  selectedId={selectedId}
                  onItemPress={onItemPress}
                  closeDropDown={closeDropDown}
                  allowParentSelection={allowParentSelection}
                />
              ))}
            </ScrollView>
          ) : null}
        </View>
      );
    }

    return (
      <View style={styles.rowContainer}>
        <TouchableOpacity
          disabled={item.isDisable}
          style={[styles.expandableRow, { paddingStart }, cornerStyle]}
          onPress={() => {
            if (isSearching) return;
            toggleAtDepth(depth, indexInParent);
          }}
        >
          <NSText
            style={{
              maxWidth: "90%",
              flexShrink: 1,
              marginEnd: 5,
              fontSize: 12,
              color: item.isDisable ? "#BDBBBF" : "#303030",
            }}
            text={item?.name}
          />
          <AntDesign
            name={isExpanded ? "caret-up" : "caret-down"}
            size={10}
            color={item.isDisable ? "#BDBBBF" : "#303030"}
          />
        </TouchableOpacity>
        {isExpanded ? (
          <ScrollView showsVerticalScrollIndicator={false}>
            {item.children!.map((child, idx) => (
              <Row
                key={`${child.id}-${depth + 1}-${idx}`}
                item={child}
                depth={depth + 1}
                indexInParent={idx}
                isFirstAtRoot={false}
                isLastAtRoot={false}
                hasSearch={hasSearch}
                isSearching={isSearching}
                openedIndices={openedIndices}
                toggleAtDepth={toggleAtDepth}
                selectedId={selectedId}
                onItemPress={onItemPress}
                closeDropDown={closeDropDown}
                allowParentSelection={allowParentSelection}
              />
            ))}
          </ScrollView>
        ) : null}
      </View>
    );
  }

  if (allowParentSelection) {
    return (
      <View
        style={[
          styles.leafRowWithSelection,
          { paddingStart },
          cornerStyle,
        ]}
      >
        <SelectionBox
          isSelected={isSelected}
          isDisabled={item.isDisable}
          onPress={() => {
            onItemPress(item, indexInParent);
          }}
        />
        <TouchableOpacity
          disabled={item.isDisable}
          style={styles.leafLabel}
          onPress={() => {
            onItemPress(item, indexInParent);
            closeDropDown();
          }}
        >
          <NSText
            style={{
              maxWidth: "90%",
              flexShrink: 1,
              fontSize: 12,
              color: item.isDisable ? "#BDBBBF" : "#303030",
            }}
            text={item?.name}
          />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <TouchableOpacity
      disabled={item.isDisable}
      style={[
        styles.leafRow,
        { paddingStart, backgroundColor: isSelected ? "#6300A35D" : "" },
        cornerStyle,
      ]}
      onPress={() => {
        onItemPress(item, indexInParent);
        closeDropDown();
      }}
    >
      <NSText
        style={{
          maxWidth: "90%",
          flexShrink: 1,
          fontSize: 12,
          color: isSelected ? "white" : item.isDisable ? "#BDBBBF" : "#303030",
        }}
        text={item?.name}
      />
    </TouchableOpacity>
  );
};

const GlobalNestedPicker = ({
  width,
  coords,
  data,
  selectedId,
  onItemPress,
  setRuntimeHeight,
  closeDropDown,
  hasSearch = false,
  hasDeepSearch = false,
  searchPlaceholder,
  allowParentSelection = false,
}: {
  width: number;
  coords: { x: number; y: number };
  data: NestedDataType[];
  selectedId?: string;
  onItemPress: (value: any, index: number) => void;
  setRuntimeHeight: (value: number) => void;
  closeDropDown: () => void;
  hasSearch?: boolean;
  hasDeepSearch?: boolean;
  searchPlaceholder?: string;
  allowParentSelection?: boolean;
}) => {
  // Accordion state generalized to arbitrary depth. openedIndices[d] is the
  // index of the currently-open child at depth d (-1 if none). Mirrors the
  // original openedIndex / openedSubIndex pair; switching open siblings at
  // depth d does not reset deeper levels, preserving the original quirk.
  const [openedIndices, setOpenedIndices] = useState<number[]>([]);
  const [searchText, setSearchText] = useState<string>("");

  const filteredData = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return data;
    return hasDeepSearch ? filterDeep(data, term) : filterShallow(data, term);
  }, [data, searchText, hasDeepSearch]);

  const isSearching = hasDeepSearch && searchText.trim().length > 0;

  const toggleAtDepth = useCallback((depth: number, index: number) => {
    setOpenedIndices(prev => {
      const next = [...prev];
      while (next.length <= depth) next.push(-1);
      next[depth] = next[depth] === index ? -1 : index;
      return next;
    });
  }, []);

  return (
    <View
      style={[styles.container, { width, top: coords.y, left: coords.x, zIndex: 1400 }]}
      onLayout={(event) => {
        setRuntimeHeight(event.nativeEvent.layout.height);
      }}
    >
      {hasSearch && (
        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder={searchPlaceholder || "Search..."}
            value={searchText}
            onChangeText={setSearchText}
            placeholderTextColor="#7D7D7D"
          />
          <Feather name="search" size={16} color="#7D7D7D" style={styles.searchIcon} />
        </View>
      )}
      <ScrollView showsVerticalScrollIndicator={false}>
        {filteredData?.map((item, index) => (
          <Row
            key={`${item.id}-${index}`}
            item={item}
            depth={0}
            indexInParent={index}
            isFirstAtRoot={index === 0}
            isLastAtRoot={index === filteredData.length - 1}
            hasSearch={hasSearch}
            isSearching={isSearching}
            openedIndices={openedIndices}
            toggleAtDepth={toggleAtDepth}
            selectedId={selectedId}
            onItemPress={onItemPress}
            closeDropDown={closeDropDown}
            allowParentSelection={allowParentSelection}
          />
        ))}
      </ScrollView>
    </View>
  );
};

export default GlobalNestedPicker;

const styles = StyleSheet?.create?.({
  container: {
    position: "absolute",
    maxHeight: 300,
    borderRadius: 5,
    backgroundColor: "#F7F2FA",
    ...shadowStyle,
  },
  rowContainer: { width: "100%" },
  expandableRow: {
    flexDirection: "row",
    padding: 10,
    alignItems: "center",
    justifyContent: "space-between",
    elevation: 5,
  },
  expandableLabel: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  leafRow: {
    padding: 10,
    elevation: 5,
  },
  leafRowWithSelection: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    elevation: 5,
  },
  leafLabel: {
    flex: 1,
  },
  selectionBoxContainer: {
    paddingEnd: 8,
  },
  selectionBox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: "#BDBBBF",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  selectionBoxSelected: {
    backgroundColor: "#6300A3",
    borderColor: "#6300A3",
  },
  selectionBoxDisabled: {
    borderColor: "#E0E0E0",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
    backgroundColor: "#F7F2FA",
    borderTopStartRadius: 5,
    borderTopEndRadius: 5,
  },
  searchInput: {
    width: "100%",
    fontSize: 14,
    color: "#303030",
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "white",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  searchIcon: {
    marginLeft: 8,
  },
});
